import { catalogServiceClient } from '~/api/client';
import { endpoints } from '~/api/endpoints';
import { endOfLocalDayISO, fromIsoDate, startOfLocalDayISO } from '~/utils/date';

import type {
  CatalogEnvelope,
  CatalogListEnvelope,
  ControlAck,
} from '../types/catalog-service.api.types';
import type { CatalogPage } from '../types/catalog-v2.types';
import type {
  ArmAssignmentListResponse,
  ArmAssignmentRetry,
  ExperimentApproval,
  ExperimentArchiveResult,
  ExperimentBulkArchive,
  ExperimentControl,
  ExperimentCreate,
  ExperimentPatch,
  ExperimentPipelineStart,
  ExperimentProgress,
  ExperimentRead,
  ExperimentRunAccepted,
  ExperimentRunStart,
  ExperimentStatusCountsRead,
  ExperimentStop,
  PipelinePreview,
  StageAttemptHistory,
} from '../types/experiments.api.types';
import type {
  ArmAssignmentPage,
  ArmAssignmentParams,
  CandidateScopeUpload,
  ExperimentListParams,
} from '../types/experiments.types';
import { toPage, toSearchParams, unwrap } from '../utils/envelope';

const exp = endpoints.catalogService.experiments;

/** Day filter `YYYY-MM-DD` → local-day ISO bound; `undefined` for an empty/bad value. */
function dayBound(day: string, edge: 'start' | 'end'): string | undefined {
  const date = fromIsoDate(day);
  if (!date) return undefined;
  return edge === 'start' ? startOfLocalDayISO(date) : endOfLocalDayISO(date);
}

/** catalog's `q` bound (Query max_length=200) — longer text 422s the list. */
export const EXPERIMENT_SEARCH_MAX_LENGTH = 200;

/** catalog's `legacyWorkflowId` bound (Query max_length=255). */
export const EXPERIMENT_LEGACY_ID_MAX_LENGTH = 255;

/**
 * One page of experiments, newest first. `status` repeats on the wire, so the
 * params are built as `URLSearchParams` rather than a flat record.
 */
export async function listExperiments(
  params: ExperimentListParams,
): Promise<CatalogPage<ExperimentRead>> {
  const searchParams = new URLSearchParams();
  const scalars = toSearchParams({
    page: params.page,
    size: params.size,
    // Clamped so a long pasted URL value can never 422 the list (the inputs cap typing too).
    q: params.q.trim().slice(0, EXPERIMENT_SEARCH_MAX_LENGTH),
    createdAfter: dayBound(params.createdAfter, 'start'),
    createdBefore: dayBound(params.createdBefore, 'end'),
    archived: params.archived,
    legacyWorkflowId: params.legacyWorkflowId.trim().slice(0, EXPERIMENT_LEGACY_ID_MAX_LENGTH),
  });
  for (const [key, value] of Object.entries(scalars)) searchParams.set(key, String(value));
  for (const status of params.status) searchParams.append('status', status);

  const body = await catalogServiceClient
    .get(exp.root(), { searchParams })
    .json<CatalogListEnvelope<ExperimentRead>>();
  return toPage(body, (row) => row);
}

/** The list's narrowing a count read shares (search, created range, legacy id). */
export type ExperimentCountScope = Pick<
  ExperimentListParams,
  'q' | 'createdAfter' | 'createdBefore' | 'legacyWorkflowId'
>;

/**
 * Every chip's count under the list's narrowing in one read (§49). Throws like any read; the
 * caller decides on a fallback (an older backend routes this path to `experiments/{uuid}` → 422).
 */
export async function getExperimentStatusCounts(
  scope: ExperimentCountScope,
): Promise<ExperimentStatusCountsRead> {
  const body = await catalogServiceClient
    .get(exp.statusCounts(), {
      searchParams: toSearchParams({
        q: scope.q.trim().slice(0, EXPERIMENT_SEARCH_MAX_LENGTH),
        createdAfter: dayBound(scope.createdAfter, 'start'),
        createdBefore: dayBound(scope.createdBefore, 'end'),
        legacyWorkflowId: scope.legacyWorkflowId.trim().slice(0, EXPERIMENT_LEGACY_ID_MAX_LENGTH),
      }),
    })
    .json<CatalogEnvelope<ExperimentStatusCountsRead>>();
  return unwrap(body);
}

/** One experiment with its scope, arm file, archive history and audit. */
export async function getExperiment(experimentId: string): Promise<ExperimentRead> {
  const body = await catalogServiceClient
    .get(exp.byId(experimentId))
    .json<CatalogEnvelope<ExperimentRead>>();
  return unwrap(body);
}

/** Status, derived phase, split state and the latest run's progress snapshot. */
export async function getExperimentProgress(experimentId: string): Promise<ExperimentProgress> {
  const body = await catalogServiceClient
    .get(exp.progress(experimentId))
    .json<CatalogEnvelope<ExperimentProgress>>();
  return unwrap(body);
}

/** SAMPLE attempts of the latest run, oldest first (404 before the first run). */
export async function getExperimentSampleAttempts(
  experimentId: string,
): Promise<StageAttemptHistory> {
  const body = await catalogServiceClient
    .get(exp.sampleAttempts(experimentId))
    .json<CatalogEnvelope<StageAttemptHistory>>();
  return unwrap(body);
}

/** Test-arm candidate count the full catalog would process (404 before the first run). */
export async function getExperimentPipelinePreview(experimentId: string): Promise<PipelinePreview> {
  const body = await catalogServiceClient
    .get(exp.pipelinePreview(experimentId))
    .json<CatalogEnvelope<PipelinePreview>>();
  return unwrap(body);
}

/** One keyset page of the arm ledger; `after` walks it, filters narrow it. */
export async function listExperimentArmAssignments(
  experimentId: string,
  params: ArmAssignmentParams,
): Promise<ArmAssignmentPage> {
  const body = await catalogServiceClient
    .get(exp.armAssignments(experimentId), {
      searchParams: toSearchParams({
        attempt: params.attempt,
        arm: params.arm,
        outlier: params.outlier,
        after: params.after,
        size: params.size,
      }),
    })
    .json<ArmAssignmentListResponse>();
  const page = toPage(body, (row) => row);
  // `attempts` is §49; an older backend omits it, and the picker falls back to 1..current.
  return { ...page, attempt: body.attempt ?? null, attempts: body.attempts ?? null };
}

/** Creates a DRAFT experiment at the wizard's first stage. 409 when the name is taken. */
export async function createExperiment(json: ExperimentCreate): Promise<ExperimentRead> {
  const body = await catalogServiceClient
    .post(exp.root(), { json })
    .json<CatalogEnvelope<ExperimentRead>>();
  return unwrap(body);
}

/** Saves wizard fields; 409 `EXPERIMENT_LOCKED` once a run consumed the experiment. */
export async function patchExperiment(
  experimentId: string,
  json: ExperimentPatch,
): Promise<ExperimentRead> {
  const body = await catalogServiceClient
    .patch(exp.byId(experimentId), { json })
    .json<CatalogEnvelope<ExperimentRead>>();
  return unwrap(body);
}

/** Hard-deletes an untouched DRAFT/CREATED experiment; 409 means "archive it instead". */
export async function deleteExperiment(experimentId: string): Promise<void> {
  await catalogServiceClient.delete(exp.byId(experimentId));
}

/** Archives or restores one experiment (409 `EXPERIMENT_WORK_IN_FLIGHT` when live). */
export async function setExperimentArchived(
  experimentId: string,
  archived: boolean,
): Promise<ExperimentRead> {
  const body = await catalogServiceClient
    .patch(exp.archived(experimentId), { json: { archived } })
    .json<CatalogEnvelope<ExperimentRead>>();
  return unwrap(body);
}

/** Archives or restores up to 100 experiments; always 200 with a per-id outcome. */
export async function bulkArchiveExperiments(
  json: ExperimentBulkArchive,
): Promise<ExperimentArchiveResult> {
  const body = await catalogServiceClient
    .post(exp.bulkArchive(), { json })
    .json<CatalogEnvelope<ExperimentArchiveResult>>();
  return unwrap(body);
}

/** Uploads a new candidate-scope generation (multipart `file` + `idType`). */
export async function uploadCandidateScope(
  experimentId: string,
  input: CandidateScopeUpload,
): Promise<ExperimentRead> {
  const formData = new FormData();
  formData.append('file', input.file);
  formData.append('idType', input.idType);
  const body = await catalogServiceClient
    .put(exp.candidateScope(experimentId), { body: formData })
    .json<CatalogEnvelope<ExperimentRead>>();
  return unwrap(body);
}

/** Clears the scope in force — the pool goes back to the filters alone. */
export async function clearCandidateScope(experimentId: string): Promise<ExperimentRead> {
  const body = await catalogServiceClient
    .delete(exp.candidateScope(experimentId))
    .json<CatalogEnvelope<ExperimentRead>>();
  return unwrap(body);
}

/** Uploads (replaces) the optional operator arm-assignment file. */
export async function uploadArmAssignmentFile(
  experimentId: string,
  file: File,
): Promise<ExperimentRead> {
  const formData = new FormData();
  formData.append('file', file);
  const body = await catalogServiceClient
    .put(exp.armAssignmentFile(experimentId), { body: formData })
    .json<CatalogEnvelope<ExperimentRead>>();
  return unwrap(body);
}

/** Starts the experiment's EXPERIMENT_GMC run (202) — also the FAILED re-run. */
export async function startExperimentRun(
  experimentId: string,
  json: ExperimentRunStart,
): Promise<ExperimentRunAccepted> {
  const body = await catalogServiceClient
    .post(exp.runs(experimentId), { json })
    .json<CatalogEnvelope<ExperimentRunAccepted>>();
  return unwrap(body);
}

/** Score gate: approve → generate the sample; SAMPLE gate: regenerate it. */
export async function generateExperimentSample(
  experimentId: string,
  json: ExperimentControl,
): Promise<ControlAck> {
  const body = await catalogServiceClient
    .post(exp.sample(experimentId), { json })
    .json<CatalogEnvelope<ControlAck>>();
  return unwrap(body);
}

/** SAMPLE gate: approve the sample and start full-catalog generation. */
export async function startExperimentPipeline(
  experimentId: string,
  json: ExperimentPipelineStart,
): Promise<ControlAck> {
  const body = await catalogServiceClient
    .post(exp.pipeline(experimentId), { json })
    .json<CatalogEnvelope<ControlAck>>();
  return unwrap(body);
}

/** APPROVAL gate: APPROVED delivers the test arm, REJECTED fails the run. */
export async function decideExperimentApproval(
  experimentId: string,
  json: ExperimentApproval,
): Promise<ControlAck> {
  const body = await catalogServiceClient
    .post(exp.approval(experimentId), { json })
    .json<CatalogEnvelope<ControlAck>>();
  return unwrap(body);
}

/** Stops the experiment as CANCELLED or COMPLETED; an in-flight run is stopped too. */
export async function stopExperiment(
  experimentId: string,
  json: ExperimentStop,
): Promise<ExperimentRead> {
  const body = await catalogServiceClient
    .post(exp.stop(experimentId), { json })
    .json<CatalogEnvelope<ExperimentRead>>();
  return unwrap(body);
}

/** Retries the latest run's FAILED arm split as a new attempt. */
export async function retryExperimentArmAssignment(
  experimentId: string,
  json: ArmAssignmentRetry,
): Promise<ControlAck> {
  const body = await catalogServiceClient
    .post(exp.armAssignment(experimentId), { json })
    .json<CatalogEnvelope<ControlAck>>();
  return unwrap(body);
}
